import _ from 'lodash';
import React from 'react';
import { Divider, Tag, Text } from 'src/@vymo/ui/atoms';
import { Card } from 'src/@vymo/ui/blocks';
import SkeletonLoader from 'src/@vymo/ui/blocks/skelton';
import { ReactComponent as ArrowDownIcon } from 'src/assets/icons/arrowDown.svg';
import { ReactComponent as GroupLineIcon } from 'src/assets/icons/groupLine.svg';
import { ReactComponent as TargetIcon } from 'src/assets/icons/target.svg';
import { Keys, locale } from 'src/i18n';
import { useAppSelector } from 'src/store/hooks';
import ErrorState from '../../components/errorState';
import Progress from '../../components/progress';
import {
  PATH_DETAILS_ACHIEVED_BY_VALUE,
  PATH_DETAILS_METRIC_TARGET_ATTRIBUTES,
  PATH_DETAILS_METRIC_TARGET_EXPECTED_PROGRESS,
} from '../../constant';
import {
  getMetricData,
  getMetricProgressColor,
  getMetricProgressPercent,
} from '../../dashboard/keyMetricsWidget/queries';
import { getKeyMetricsDetails, getStatus } from '../../selector';
import styles from '../index.module.scss';

function KeyMetricsHeader() {
  const details = useAppSelector(getKeyMetricsDetails);
  const status = useAppSelector((state) =>
    getStatus(state, 'key_metrics_details'),
  );

  const { total, actual } = getMetricData({ data: { body: details } });

  const progressPercentage = getMetricProgressPercent({
    data: { body: details },
  });

  const progressColor = getMetricProgressColor({ data: { body: details } });
  const metricAttributes = _.get(
    details,
    PATH_DETAILS_METRIC_TARGET_ATTRIBUTES,
    [],
  );

  const shortfall =
    metricAttributes.find((attr) => attr.label === 'Shortfall')?.value ||
    locale(Keys.DASH);

  const expectedTarget = _.get(
    details,
    PATH_DETAILS_METRIC_TARGET_EXPECTED_PROGRESS,
    locale(Keys.DASH),
  );
  const achievedBy = _.get(details, PATH_DETAILS_ACHIEVED_BY_VALUE, '');

  return (
    <Card>
      <Card classNames={styles.details__headerCard}>
        {status === 'loading' && <SkeletonLoader avtar lines={5} />}
        {status === 'error' && <ErrorState />}
        {status === 'done' && (
          <>
            <Tag>{details?.card?.frequency}</Tag>

            <div className={styles.details__headerCard__stats}>
              <Text type="h4">
                {actual} {total ? `/ ${total}` : ''}
              </Text>{' '}
              {!_.isNil(progressPercentage) && (
                <div
                  style={{ color: progressColor }}
                  className={styles.keyMetricsTable__percent}
                >
                  <Progress
                    color={progressColor}
                    progress={progressPercentage}
                  />
                  {progressPercentage}%
                </div>
              )}
            </div>

            <Divider />

            <div className={styles.details__headerCard__target}>
              <div className={styles.details__headerCard__target__text}>
                <TargetIcon />
                <span>
                  <Text>{expectedTarget}</Text>
                  <Text
                    classNames={
                      styles.details__headerCard__target__text__placeholder
                    }
                  >
                    {' '}
                    {locale(Keys.EXPECTED_TARGET)}
                  </Text>
                </span>
              </div>
              {Boolean(shortfall) && (
                <div className={styles.details__headerCard__target__text}>
                  <ArrowDownIcon
                    className={
                      styles.details__headerCard__target__shortfallIcon
                    }
                  />
                  <span>
                    <Text>{shortfall}</Text>
                    <Text
                      classNames={
                        styles.details__headerCard__target__text__placeholder
                      }
                    >
                      {' '}
                      {locale(Keys.SHORTFALL)}
                    </Text>
                  </span>
                </div>
              )}
            </div>
            <div className={styles.details__headerCard__target__text}>
              <GroupLineIcon />
              <span>
                <Text>{achievedBy}</Text>
                <Text
                  classNames={
                    styles.details__headerCard__target__text__placeholder
                  }
                >
                  {' '}
                  {locale(Keys.PEOPLE_ACHIEVED)}
                </Text>
              </span>
            </div>
          </>
        )}
      </Card>
    </Card>
  );
}

export default KeyMetricsHeader;
